"use client"

// src/components/auth/configuracion/VendedorModal.tsx
import type React from "react"
import { useState, useEffect } from "react"
import { api } from "../../../services/Axios"
import ConfirmationModal from "../../utils/avisos/ConfirmationModal"
import { useNavigate } from "react-router-dom"
import MessageModal from "../../utils/avisos/MessageModal"
import Modal from "../../ui/Modal"
import Input from "../../ui/Input"
import PasswordInput from "../../ui/PasswordInput"
import Button from "../../ui/Button"
import { FaSave, FaTimes, FaUser, FaEnvelope, FaLock, FaShieldAlt, FaExclamationTriangle } from "react-icons/fa"

interface AddOrEditVendedorModalProps {
  isOpen: boolean
  onClose: () => void
  onVendedorSaved: () => void
  vendedorToEdit?: { nombre: string; email: string; password?: string } | null
}

const AddOrEditVendedorModal: React.FC<AddOrEditVendedorModalProps> = ({
  isOpen,
  onClose,
  onVendedorSaved,
  vendedorToEdit,
}) => {
  const [nombre, setNombre] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [nombreError, setNombreError] = useState<string>("")
  const [emailError, setEmailError] = useState<string>("")
  const [passwordError, setPasswordError] = useState<string>("")

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false)
  const [messageContent, setMessageContent] = useState<string>("")
  const [messageType, setMessageType] = useState<"success" | "error">("success")
  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen) {
      if (vendedorToEdit) {
        setNombre(vendedorToEdit.nombre)
        setEmail(vendedorToEdit.email)
        setPassword(vendedorToEdit.password || "")
      } else {
        setNombre("")
        setEmail("")
        setPassword("")
      }
      setNombreError("")
      setEmailError("")
      setPasswordError("")
      setMessageContent("")
      setShowMessageModal(false)
    }
  }, [isOpen, vendedorToEdit])

  const handleSaveVendedor = async () => {
    let hasError = false

    if (!nombre) {
      setNombreError("Este campo es obligatorio.")
      hasError = true
    } else {
      setNombreError("")
    }

    if (!email) {
      setEmailError("Este campo es obligatorio.")
      hasError = true
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      setEmailError("Ingrese un email válido.")
      hasError = true
    } else {
      setEmailError("")
    }

    if (!password) {
      setPasswordError("Este campo es obligatorio.")
      hasError = true
    } else {
      setPasswordError("")
    }

    if (hasError) {
      setMessageContent("Por favor, completa todos los campos obligatorios.")
      setMessageType("error")
      setShowMessageModal(true)
      return
    }

    if (!hasError) {
      setIsSubmitting(true)
      try {
        if (vendedorToEdit) {
          await api.put("/api/auth/user/edit-vendedor", { nombre, email, password })
          setMessageContent("Vendedor actualizado exitosamente.")
        } else {
          await api.post("/api/auth/user/add-vendedor", { nombre, email, password })
          localStorage.setItem("email", email)
          navigate("/verificacion")
        }
        setMessageType("success")
        setShowMessageModal(true)
      } catch (error) {
        setMessageContent("Error al guardar el vendedor.")
        setMessageType("error")
        setShowMessageModal(true)
      } finally {
        setIsSubmitting(false)
        setShowConfirmationModal(false)
      }
    }
  }

  const closeMessageModal = () => {
    setShowMessageModal(false)
    if (messageType === "success") {
      onVendedorSaved()
      onClose()
    }
  }

  const confirmSaveVendedor = () => setShowConfirmationModal(true)

  const modalTitle = vendedorToEdit ? "Editar Cajero" : "Agregar Cajero"

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="md">

        <div className="p-4">
          {/* Introduction text */}
          <div className="mb-6 bg-blue-50 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <FaShieldAlt className="h-5 w-5 text-[#2d53da]" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-700">
                  {vendedorToEdit
                    ? "Actualice la información del cajero. Los cajeros tienen acceso limitado al sistema."
                    : "Los cajeros tienen acceso limitado al sistema y solo pueden realizar operaciones de venta."}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="flex items-start">
              <div className="pt-4 pr-2">
                <FaUser className="text-[#2d53da] text-lg" />
              </div>
              <div className="flex-1">
                <Input
                  label="Nombre"
                  value={nombre}
                  onChange={(e) => {
                    setNombre(e.target.value)
                    if (e.target.value) setNombreError("")
                  }}
                  error={nombreError}
                  fullWidth
                  required
                />
              </div>
            </div>

            <div className="flex items-start">
              <div className="pt-4 pr-2">
                <FaEnvelope className="text-[#2d53da] text-lg" />
              </div>
              <div className="flex-1">
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (e.target.value) setEmailError("")
                  }}
                  error={emailError}
                  fullWidth
                  required
                />
              </div>
            </div>

            <div className="flex items-start">
              <div className="pt-4 pr-2">
                <FaLock className="text-[#2d53da] text-lg" />
              </div>
              <div className="flex-1">
                <PasswordInput
                  label="Contraseña"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (e.target.value) setPasswordError("")
                  }}
                  error={passwordError}
                  fullWidth
                  showStrengthIndicator
                  required
                />
              </div>
            </div>

            {/* Error message */}
            {(nombreError || emailError || passwordError) && (
              <div className="mt-4 p-3 bg-red-50 rounded-md flex items-start">
                <FaExclamationTriangle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-red-600">Por favor, complete todos los campos requeridos correctamente.</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                onClick={onClose}
                variant="outline"
                icon={FaTimes}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmSaveVendedor}
                disabled={isSubmitting}
                icon={FaSave}
                className="bg-[#2d53da] hover:bg-[#2d53da]/90 text-white"
              >
                {isSubmitting ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={showConfirmationModal}
        title={vendedorToEdit ? "Confirmar edición" : "Confirmar adición"}
        message={`¿Está seguro de que desea ${vendedorToEdit ? "editar" : "agregar"} este vendedor?`}
        onConfirm={handleSaveVendedor}
        onCancel={() => setShowConfirmationModal(false)}
      />

      <MessageModal
        isOpen={showMessageModal}
        message={messageContent}
        messageType={messageType}
        onClose={closeMessageModal}
      />
    </>
  )
}

export default AddOrEditVendedorModal

